import React from 'react';
import styled from 'styled-components';
import { ArrowRight, MessageSquareQuote, PlaySquare, Sparkles, UserRound, HeartHandshake } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { Card, Chip, Grid, Label, Muted, PillarBlock, Section } from '../parts';
import { FORMAT_LABELS, GOAL_LABELS } from '../labels';

export default function AudienceTab({ analysis, pillarsByTab }) {
    const { result, previous } = analysis;
    const { community } = result;

    return (
        <>
            <Section icon={UserRound} title='Quem te acompanha' subtitle={community.verdict}>
                <Journey>
                    <Card>
                        <Label>Antes de te encontrar</Label>
                        <Muted>{community.journey.before}</Muted>
                    </Card>
                    <JourneyArrow><ArrowRight size={18} /></JourneyArrow>
                    <Card>
                        <Label>Depois</Label>
                        <Muted>{community.journey.after}</Muted>
                    </Card>
                </Journey>

                <Grid $min='240px'>
                    <Card>
                        <Label>Quem é</Label>
                        <Muted>{community.persona.who}</Muted>
                    </Card>
                    <Card>
                        <Label>O que sente</Label>
                        <Muted>{community.persona.feels}</Muted>
                    </Card>
                    <Card>
                        <Label>O que busca</Label>
                        <Muted>{community.persona.seeks}</Muted>
                    </Card>
                </Grid>
            </Section>

            {pillarsByTab.map(pillar => (
                <Section key={pillar.key} icon={Sparkles} title={pillar.title}>
                    <PillarBlock pillar={pillar} previousScore={previous?.pillars?.[pillar.key]} />
                </Section>
            ))}

            <Section icon={PlaySquare} title='O que cada tipo de post entrega'>
                <Card style={{ padding: 0, overflowX: 'auto' }}>
                    <Table>
                        <thead>
                            <tr>
                                <th>Conteúdo</th>
                                <th>Serve para</th>
                                <th>Resultado</th>
                                <th>O que fazer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {community.formats.map((format, index) => (
                                <tr key={index}>
                                    <td>
                                        <strong>{format.theme}</strong>
                                        <FormatName>{FORMAT_LABELS[format.format]}</FormatName>
                                    </td>
                                    <td><Chip>{GOAL_LABELS[format.goal]}</Chip></td>
                                    <td>{format.performance}</td>
                                    <td>{format.recommendation}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            </Section>

            <Section icon={MessageSquareQuote} title='O que dizem nos comentários' subtitle={community.intent_signals.verdict}>
                {community.voice.length > 0 && (
                    <Card>
                        <Label>Expressões que mais aparecem</Label>
                        <Words>
                            {community.voice.map((term, index) => (
                                <Word key={term} $weight={index < 3 ? 'strong' : 'normal'}>{term}</Word>
                            ))}
                        </Words>
                    </Card>
                )}

                {community.intent_signals.quotes.length > 0 && (
                    <Grid $min='240px'>
                        {community.intent_signals.quotes.map((quote, index) => (
                            <Quote key={index}>
                                <HeartHandshake size={15} color={panelAccent(colors.secondaryPurple)} />
                                <span>{quote}</span>
                            </Quote>
                        ))}
                    </Grid>
                )}
            </Section>
        </>
    );
}

const Journey = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 10px;
    align-items: stretch;

    @media screen and (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const JourneyArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${panelAccent(colors.secondaryPurple)};

    @media screen and (max-width: 700px) {
        transform: rotate(90deg);
    }
`;

const Table = styled.table`
    width: 100%;
    min-width: 640px;
    border-collapse: collapse;
    font-size: 13px;
    color: ${colors.night};

    th {
        text-align: left;
        font-size: 12px;
        font-weight: 600;
        color: ${colors.nightLight};
        padding: 12px 16px;
        border-bottom: 1px solid ${colors.neutralDark};
    }

    td {
        padding: 12px 16px;
        vertical-align: top;
        line-height: 1.5;
        border-bottom: 1px solid ${colors.neutralDark};
    }

    tr:last-child td {
        border-bottom: none;
    }

    strong {
        display: block;
        color: ${colors.nightDark};
        font-weight: 600;
    }
`;

const FormatName = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const Words = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
`;

const Word = styled.span`
    padding: 6px 12px;
    border-radius: 8px;
    font-size: ${({ $weight }) => ($weight === 'strong' ? '15px' : '13px')};
    font-weight: ${({ $weight }) => ($weight === 'strong' ? 600 : 400)};
    background: ${({ $weight }) => ($weight === 'strong' ? panelAccentAlpha(colors.secondaryPurple, '14') : colors.neutral)};
    color: ${({ $weight }) => ($weight === 'strong' ? panelAccent(colors.secondaryPurple) : colors.nightDark)};
`;

const Quote = styled(Card)`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.nightDark};

    svg {
        flex-shrink: 0;
        margin-top: 3px;
    }
`;
